import React, { useState, useEffect } from "react";
import {
  PlusOutlined,
} from '@ant-design/icons';
import 'react-phone-input-2/lib/style.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Breadcrumb, Form, Input, Button, Tabs, Row, Col, Select, Switch, Upload, message, Space, Divider, Tag, TimePicker, notification } from "antd";
import { UploadOutlined, SaveOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Link } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import { resetShiftData, fetchShiftList, createShift, updateShift, deleteShift } from "../../slices/shift";
import { fetchSettingList, saveCompanySetting, saveFinancialSetting, saveTaxComplianceSetting, saveNotificationSetting, saveTermsConditionSetting } from "../../slices/setting";
import { ReactComponent as Back } from '../../Images/Back.svg';
import initialTermsCondition from "../../Common/intialTermsCondition";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const DAY_SECONDS = 24 * 3600;
const format = "hh:mm:ss A";

const MAX_SIZE_MB = 2;
const ALLOWED_EXT = ["png", "svg"];
const TAX_ALLOWED_EXT = ["pdf", "doc", "docx"];

const { TabPane } = Tabs;
const { Option } = Select;

// convert dayjs value to seconds (works with 12h AM/PM format)
function toSecondsFromDayjs(value) {
  if (!value) return null;
  const hh = value.hour(), mm = value.minute(), ss = value.second();
  return hh * 3600 + mm * 60 + ss;
}

// split wrap-around shifts into linear ranges
function splitRange(startS, endS) {
  if (startS == null || endS == null) return [];
  if (endS >= startS) return [[startS, endS]];
  return [[startS, DAY_SECONDS - 1], [0, endS]];
}

// merge ranges and return merged array
function mergeRanges(ranges) {
  if (!ranges || ranges.length === 0) return [];
  const sorted = ranges.slice().sort((a, b) => a[0] - b[0]);
  const merged = [sorted[0].slice()];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i], last = merged[merged.length - 1];
    if (cur[0] <= last[1] + 1) last[1] = Math.max(last[1], cur[1]);
    else merged.push(cur.slice());
  }
  return merged;
}

// compute duration seconds (handles wrap)
function durationSecs(startS, endS) {
  if (startS == null || endS == null) return 0;
  if (endS >= startS) return endS - startS + 1; // +1 for inclusive counting (optional)
  return (DAY_SECONDS - startS) + endS + 1;
}

export default function Setting() {
  const dispatch = useDispatch();
  const [companyForm] = Form.useForm();
  const [shiftForm] = Form.useForm();
  const [financeForm] = Form.useForm();
  const [taxForm] = Form.useForm();
  const [notifForm] = Form.useForm();
  const [termsForm] = Form.useForm();
  const [activeKey, setActiveKey] = useState("company");
  const [durationsByIdx, setDurationsByIdx] = useState({}); // {0: "8h 0m", 1: "6h 30m"}
  const [coverage, setCoverage] = useState({ total: 0, merged: [] });
  const { data = [], loading: shiftLoading } = useSelector(state => state.shift);
  const { data: settingData, loading: settingLoading } = useSelector(state => state.setting);
  const [saveStatuses, setSaveStatuses] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [taxFileList, setTaxFileList] = useState([]);
  const [quotationTermValue, setQuotationTermValue] = useState();
  const [dieQuotationTermValue, setDieQuotationTermValue] = useState();
  const [workOrderTermValue, setWorkOrderTermValue] = useState();
  const [proformaTermValue, setProformaTermValue] = useState();

  const getFileObject = (uploadFile) => uploadFile?.originFileObj || uploadFile;
  const checkImageDimension = (file, expectedW = 200, expectedH = 60) => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onerror = () => {
        console.debug("FileReader error");
        resolve(false);
      };

      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // compare exact pixel dimensions
          resolve(img.width === expectedW && img.height === expectedH);
        };
        img.onerror = () => {
          console.debug("Image load error for dimension check");
          resolve(false);
        };
        // use the data URL so both raster and svg load reliably
        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    });
  };

  // const validateLogo = async (_, value) => {
  //   // value might be the fileList array (controlled) or undefined
  //   if (!value || value.length === 0) {
  //     return Promise.reject(new Error("Please upload company logo"));
  //   }

  //   const uploadFile = value[0]; // only one allowed (maxCount=1)
  //   const file = getFileObject(uploadFile);

  //   if (!file) {
  //     return Promise.reject(new Error("Invalid file"));
  //   }

  //   // type/extension check (defensive: some svg files may have empty mime type)
  //   const name = (file.name || "").toLowerCase();
  //   const ext = name.split(".").pop();

  //   if (!ext || !ALLOWED_EXT.includes(ext)) {
  //     return Promise.reject(new Error("Only PNG, SVG, JPG or JPEG files are allowed"));
  //   }

  //   // size check
  //   // const isLt2M = file.size / 1024 / 1024 <= MAX_SIZE_MB;
  //   // if (!isLt2M) {
  //   //   return Promise.reject(new Error("File must be 2 MB or smaller"));
  //   // }

  //   // dimension check
  //   const okDim = await checkImageDimension(file, 200, 60);
  //   if (!okDim) {
  //     return Promise.reject(new Error("Image must be exactly 200 × 60 pixels"));
  //   }

  //   return Promise.resolve();
  // };
  const validateLogo = async (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject(new Error("Please upload company logo"));
    }

    const uploadFile = value[0];

    // ✅ EXISTING LOGO (from backend) → SKIP validation
    if (!uploadFile.originFileObj && uploadFile.url) {
      return Promise.resolve();
    }

    // ✅ NEW UPLOADED FILE
    const file = uploadFile.originFileObj;

    if (!(file instanceof Blob)) {
      return Promise.reject(new Error("Invalid file"));
    }

    const name = file.name.toLowerCase();
    const ext = name.split(".").pop();

    if (!ALLOWED_EXT.includes(ext)) {
      return Promise.reject(new Error("Only PNG or SVG files are allowed"));
    }

    // 🔥 Dimension check ONLY for new file
    // const okDim = await checkImageDimension(file, 200, 60);
    // if (!okDim) {
    //   return Promise.reject(new Error("Image must be exactly 200 × 60 pixels"));
    // }

    return Promise.resolve();
  };

  const validateTaxDocuments = async (_, value) => {
    // ✅ NOT REQUIRED
    if (!value || value.length === 0) {
      return Promise.resolve();
    }

    for (const uploadFile of value) {
      // ✅ EXISTING FILE FROM BACKEND → skip
      if (!uploadFile.originFileObj && uploadFile.url) {
        continue;
      }

      const file = uploadFile.originFileObj;

      if (!(file instanceof Blob)) {
        return Promise.reject(new Error("Invalid file"));
      }

      const ext = file.name.split(".").pop().toLowerCase();

      if (!TAX_ALLOWED_EXT.includes(ext)) {
        return Promise.reject(
          new Error("Only PDF, DOC, or DOCX files are allowed")
        );
      }
    }

    return Promise.resolve();
  };

  const handleChange = ({ fileList: newList }) => {
    setFileList(newList);
    companyForm.setFieldsValue({ company_logo: newList });
  };

  const handleTaxDocChange = ({ fileList }) => {
    setTaxFileList(fileList);
    taxForm.setFieldsValue({ compliance_document: fileList });
  };

  const handleQuotationTermsChange = value => {
    setQuotationTermValue(value);
  };
   const handleWorkOrderTermsChange = value => {
    setWorkOrderTermValue(value);
  };
  

  useEffect(() => {
    dispatch(fetchSettingList({ page: 1, pagesize: 10 }));
  }, [dispatch]);

  useEffect(() => {
    console.log(settingData)
    if (!settingData) return;

    const settings = settingData;

    /* ================= COMPANY TAB ================= */
    companyForm.setFieldsValue({
      company_name: settings?.company_settings?.company_name,
      legal_name: settings?.company_settings?.legal_name,
      email: settings?.company_settings?.email,
      phone: settings?.company_settings?.phone,
      address: settings?.company_settings?.address,
      website: settings?.company_settings?.website,
    });

    /* ================= FINANCIAL TAB ================= */
    financeForm.setFieldsValue({
      fiscal_year_start: settings?.financial_settings?.fiscal_year_start,
      invoice_prefix: settings?.financial_settings?.invoice_prefix,
      payment_terms: settings?.financial_settings?.payment_terms,
      rounding_precision: settings?.financial_settings?.rounding_precision,
      bank_name: settings?.financial_settings?.bank_name,
      account_number: settings?.financial_settings?.account_number,
      bank_address: settings?.financial_settings?.bank_address,
      ifsc_code: settings?.financial_settings?.ifsc_code,
    });

    /* ================= TAX TAB ================= */
    taxForm.setFieldsValue({
      tax_registration_no: settings?.tax_compliance_settings?.tax_registration_no,
      pan_number: settings?.tax_compliance_settings?.pan_number,
      default_tax_rate: settings?.tax_compliance_settings?.default_tax_rate,
      tax_rounding_method: settings?.tax_compliance_settings?.tax_rounding_method || "On_total",
      tax_report_email: settings?.tax_compliance_settings?.tax_report_email,
    });

    /* ================= NOTIFICATION TAB ================= */
    notifForm.setFieldsValue({
      email_notifications: settings?.notification_settings?.email_notifications,
      push_notifications: settings?.notification_settings?.push_notifications,
      daily_summary: settings?.notification_settings?.daily_summary,
      smtp_server: settings?.notification_settings?.smtp_server,
      system_email: settings?.notification_settings?.system_email,
      alert_emails: settings?.notification_settings?.alert_emails,
    });

     /* ================= TERMS & CONDITION TAB ================= */
    setQuotationTermValue(settings?.term_and_condition_settings?.quotation_terms_and_conditions);
    setWorkOrderTermValue(settings?.term_and_condition_settings?.work_order_terms_and_conditions);
    setDieQuotationTermValue(settings?.term_and_condition_settings?.die_terms_and_conditions);
    setProformaTermValue(settings?.term_and_condition_settings?.proforma_terms_and_conditions);

    /* ================= LOGO ================= */
    if (settings?.company_settings?.company_logo) {
      const existingLogo = {
        uid: "-1",
        name: "company-logo.png",
        status: "done",
        url: settings?.company_settings?.company_logo,
      };

      setFileList([existingLogo]);
      companyForm.setFieldsValue({
        company_logo: [existingLogo],
      });
    }

    if (settings?.tax_compliance_settings?.compliance_document) {
      const existingDoc = {
        uid: "-1",
        name: "compliance-document.pdf",
        status: "done",
        url: settings?.tax_compliance_settings?.compliance_document,
      };

      setTaxFileList([existingDoc]);
      taxForm.setFieldsValue({
        compliance_document: [existingDoc],
      });
    }
  }, [settingData]);

  const hasNewLogo = () => {
    return fileList?.[0]?.originFileObj instanceof Blob;
  };

  const computeDurationsFromForm = () => {
    const values = shiftForm.getFieldValue("shifts") || []; // ensure correct path
    const allRanges = [];
    const per = {};

    values.forEach((s, idx) => {
      const startS = toSecondsFromDayjs(s?.start_time);
      const endS = toSecondsFromDayjs(s?.end_time);
      if (startS == null || endS == null) {
        per[idx] = "—";
      } else {
        const parts = splitRange(startS, endS);
        parts.forEach(p => allRanges.push(p));
        const dur = durationSecs(startS, endS);
        per[idx] = fmtSecsToLabel(dur);
      }
    });

    const merged = mergeRanges(allRanges);
    let total = 0;
    merged.forEach(([a, b]) => { total += (b - a + 1); });

    setDurationsByIdx(per);
    setCoverage({ total, merged });
  }
  useEffect(() => {
    (async () => {
      dispatch(resetShiftData());
      const res = await dispatch(fetchShiftList({ page: 1, pagesize: 20 }));
      if (res?.payload) {
        shiftForm.setFieldsValue({
          shifts: res.payload?.results?.map(s => ({
            id: s.id,
            shift_name: s.shift_name,
            start_time: dayjs(s.start_time, "HH:mm:ss"),
            end_time: dayjs(s.end_time, "HH:mm:ss")
          }))
        });
        computeDurationsFromForm();
      }
    })();
  }, [dispatch]);


  const fmtSecsToLabel = (secs) => {
    if (!secs || secs <= 0) return "0h 0m";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const onValuesChange = (changed, allValues) => {
    const shifts = allValues.shifts || [];
    const allRanges = [];
    const per = {};

    shifts.forEach((s, idx) => {
      const startS = toSecondsFromDayjs(s?.start_time);
      const endS = toSecondsFromDayjs(s?.end_time);
      if (startS == null || endS == null) {
        per[idx] = "—";
        return;
      }
      const parts = splitRange(startS, endS);
      parts.forEach(p => allRanges.push(p));
      const dur = durationSecs(startS, endS);
      per[idx] = fmtSecsToLabel(dur);
    });

    const merged = mergeRanges(allRanges);
    let total = 0;
    merged.forEach(([a, b]) => { total += (b - a + 1); });

    setDurationsByIdx(per);
    setCoverage({ total, merged });
  }

  const handleShiftSave = async values => {
    const shifts = values.shifts || [];

    // init statuses
    setSaveStatuses(shifts.map(() => ({ status: "saving", msg: null })));

    try {
      // prepare requests (dispatch returns a Promise resolving to an action)
      const requests = shifts.map((item, idx) => {
        const payload = {
          shift_name: item.shift_name,
          start_time: item.start_time?.format("HH:mm:ss") ?? "",
          end_time: item.end_time?.format("HH:mm:ss") ?? ""
        };
        return item.id
          ? dispatch(updateShift({ id: item.id, data: payload }))
          : dispatch(createShift(payload));
      });

      // Wait for all to complete (they resolve to action objects for RTK thunks)
      const results = await Promise.allSettled(requests);

      // compute statuses and collect messages
      const newStatuses = [];
      const failureDetails = [];

      for (let i = 0; i < results.length; i++) {
        const res = results[i];

        // by default treat as failed unless we detect success
        let status = "failed";
        let msg = null;

        if (res.status === "fulfilled") {
          const action = res.value; // RTK action

          // If thunk was rejected, action.type endsWith('/rejected')
          if (typeof action?.type === "string" && action.type.endsWith("/rejected")) {
            // best effort extract message
            msg =
              action.payload ||
              action.error?.message ||
              (action?.meta?.requestId && "Server rejected the request") ||
              JSON.stringify(action);
            status = "failed";
          } else {
            // success
            status = "success";
          }

          // also some APIs return { success: false, message: "..."} with 200 — handle that:
          if (action?.payload && typeof action.payload === "object") {
            if (action.payload.success === false || action.payload?.status === "error") {
              // treat as failed with server message
              status = "failed";
              msg = msg || action.payload.message || JSON.stringify(action.payload);
            }
          }
        } else {
          // the dispatch call itself rejected (network error, etc.)
          const reason = res.reason;
          msg =
            reason?.payload ||
            reason?.error?.message ||
            (reason && reason.toString()) ||
            "Network or unexpected error";
          status = "failed";
        }

        newStatuses[i] = { status, msg };
        if (status === "failed") {
          // gather a readable message
          failureDetails.push(`Shift ${i + 1}: ${msg || "Unknown error"}`);
        }
      }

      // update UI statuses
      setSaveStatuses(newStatuses);

      if (failureDetails.length > 0) {
        // show notification with details (collapsible or multiline)
        notification.error({
          message: "Some shifts failed to save",
          description: (
            <div>
              {failureDetails.map((t, idx) => (
                <div key={idx} style={{ marginBottom: 6 }}>
                  {t}
                </div>
              ))}
              <div style={{ marginTop: 6, color: "rgba(0,0,0,0.45)" }}>
                Check rows highlighted in red for more details.
              </div>
            </div>
          ),
          duration: 8
        });
      } else {
        message.success("All shifts saved successfully");
        // reload shifts
        dispatch(fetchShiftList({ page: 1, pagesize: 20 }));
        // reset statuses to success (optional)
        setSaveStatuses(shifts.map(() => ({ status: "success", msg: null })));
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Unexpected save error",
        description: err.message || String(err)
      });
      // mark all failed
      setSaveStatuses(shifts.map(() => ({ status: "failed", msg: err.message || null })));
    }
  };

  const onSaveCompanySetting = async (values) => {
    try {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (key !== "company_logo") {
          formData.append(key, value ?? "");
        }
      });

      if (hasNewLogo()) {
        formData.append("company_logo", fileList[0].originFileObj);
      }

      dispatch(saveCompanySetting({ payload: formData }))
        .unwrap()
        .then(result => {
          if (result.success) {
            message.success(result?.message);
          }
        })
        .catch(error => {
          if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
              if (Array.isArray(error[key])) {
                messages.push(`${key}: ${error[key].join(', ')}`);
              }
            }

            if (messages.length > 0) {
              message.error(messages.join('; '));
            } else {
              message.error('An unknown error occurred.');
            }
          } else if (typeof error === 'string') {
            message.error(error);
          } else if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('An unknown error occurred.');
          }
        });
    } catch (err) {
      message.error("Please fix validation errors before saving.");
      // If you want, switch to the first invalid tab
      console.warn(err);
    }
  };
  const onSaveFinancialSetting = async (values) => {
    try {
      dispatch(saveFinancialSetting({ payload: values }))
        .unwrap()
        .then(result => {
          if (result.success) {
            message.success(result?.message);
          }
        })
        .catch(error => {
          if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
              if (Array.isArray(error[key])) {
                messages.push(`${key}: ${error[key].join(', ')}`);
              }
            }

            if (messages.length > 0) {
              message.error(messages.join('; '));
            } else {
              message.error('An unknown error occurred.');
            }
          } else if (typeof error === 'string') {
            message.error(error);
          } else if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('An unknown error occurred.');
          }
        });
    } catch (err) {
      message.error("Please fix validation errors before saving.");
      // If you want, switch to the first invalid tab
      console.warn(err);
    }
  }
  const onSaveTaxComplainceSetting = async (values) => {
    try {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (key !== "compliance_document") {
          formData.append(key, value ?? "");
        }
      });

      taxFileList.forEach((file) => {
        if (file.originFileObj instanceof Blob) {
          formData.append("compliance_document", file.originFileObj);
        }
      });

      dispatch(saveTaxComplianceSetting({ payload: formData }))
        .unwrap()
        .then(result => {
          if (result.success) {
            message.success(result?.message);
          }
        })
        .catch(error => {
          if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
              if (Array.isArray(error[key])) {
                messages.push(`${key}: ${error[key].join(', ')}`);
              }
            }

            if (messages.length > 0) {
              message.error(messages.join('; '));
            } else {
              message.error('An unknown error occurred.');
            }
          } else if (typeof error === 'string') {
            message.error(error);
          } else if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('An unknown error occurred.');
          }
        });
    } catch (err) {
      message.error("Please fix validation errors before saving.");
      // If you want, switch to the first invalid tab
      console.warn(err);
    }
  };

  const onSaveNotificationSetting = async (values) => {
    try {
      dispatch(saveNotificationSetting({ payload: values }))
        .unwrap()
        .then(result => {
          if (result.success) {
            message.success(result?.message);
          }
        })
        .catch(error => {
          if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
              if (Array.isArray(error[key])) {
                messages.push(`${key}: ${error[key].join(', ')}`);
              }
            }

            if (messages.length > 0) {
              message.error(messages.join('; '));
            } else {
              message.error('An unknown error occurred.');
            }
          } else if (typeof error === 'string') {
            message.error(error);
          } else if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('An unknown error occurred.');
          }
        });
    } catch (err) {
      message.error("Please fix validation errors before saving.");
      // If you want, switch to the first invalid tab
      console.warn(err);
    }
  }

  const onSaveTermsConditionSetting = async (values) => {
    try {
      dispatch(saveTermsConditionSetting({ payload: values }))
        .unwrap()
        .then(result => {
          if (result.success) {
            message.success(result?.message);
          }
        })
        .catch(error => {
          if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
              if (Array.isArray(error[key])) {
                messages.push(`${key}: ${error[key].join(', ')}`);
              }
            }

            if (messages.length > 0) {
              message.error(messages.join('; '));
            } else {
              message.error('An unknown error occurred.');
            }
          } else if (typeof error === 'string') {
            message.error(error);
          } else if (error instanceof Error) {
            message.error(error.message);
          } else {
            message.error('An unknown error occurred.');
          }
        });
    } catch (err) {
      message.error("Please fix validation errors before saving.");
      // If you want, switch to the first invalid tab
      console.warn(err);
    }
  }
  // const onSaveAll = async () => {
  //   try {
  //     const company = await companyForm.validateFields();
  //     const finance = await financeForm.validateFields();
  //     const tax = await taxForm.validateFields();
  //     const notif = await notifForm.validateFields();

  //     const payload = {
  //       company,
  //       finance,
  //       tax,
  //       notif,
  //       logo: logoFile ? logoFile.name || "uploaded" : null,
  //     };

  //     // Replace with real API call
  //     console.log("Saving settings payload ->", payload);
  //     message.success("Settings saved successfully.");
  //   } catch (err) {
  //     message.error("Please fix validation errors before saving.");
  //     // If you want, switch to the first invalid tab
  //     console.warn(err);
  //   }
  // };

  const isFullCoverage = coverage.total >= DAY_SECONDS;
  const totalHours = (coverage.total / 3600).toFixed(2);

  return (
    <>
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>Settings</h2>
            <div className="bredcrumbwrp">
              <Link to="/dashboard" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Settings' },
                ]}
              />
            </div>
          </Col>
        </Row>
      </div>
      <div style={{ minHeight: '100vh', background: '#fff', padding: 16 }} className="p-4">
        {/* Top action bar */}

        <Tabs activeKey={activeKey} onChange={setActiveKey} type="line" tabPosition="left">
          <TabPane tab="Company" key="company">
            <Form form={companyForm} layout="vertical" onFinish={onSaveCompanySetting}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="company_name" label="Company Name" rules={[{ required: true, message: "Please enter company name" }]}>
                    <Input placeholder="Alux Pvt Ltd" />
                  </Form.Item>
                  <Form.Item name="legal_name" label="Legal Name">
                    <Input placeholder="Alux Pvt Ltd - Legal" />
                  </Form.Item>

                  <Form.Item name="email" label="Support Email" rules={[{ type: "email", message: "Enter a valid email" }]}>
                    <Input placeholder="demo@aluxerp.com" />
                  </Form.Item>

                  {/* <Form.Item name="phone" label="Phone">
                    <Input placeholder="+91 98765 43210" />
                  </Form.Item> */}
                  <Form.Item
                    label="Mobile Number"
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: 'Please input your phone number!',
                      },
                    ]}
                  >
                    <PhoneInput
                      country={'in'}
                      enableSearch={true}
                      placeholder="Enter phone number"
                      onChange={value => value}
                    />
                  </Form.Item>

                  <Form.Item name="address" label="Address">
                    <Input.TextArea rows={3} placeholder="Head office address" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="company_logo"
                    label="Company Logo"
                    required
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e && e.fileList}
                    rules={[{ validator: validateLogo }]}
                  >
                    <Upload
                      beforeUpload={() => false}
                      listType="picture-card"
                      accept=".svg,.png"
                      maxCount={1}
                      onChange={handleChange}
                      fileList={fileList}
                      showUploadList={{
                        showPreviewIcon: false,
                        showRemoveIcon: true,
                      }}
                    >
                     {fileList.length < 1 && (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                    </Upload>
                    <div className="mt-2 text-sm">Recommended size: 200x60px (PNG/SVG)</div>
                  </Form.Item>

                  <Divider />

                  <Form.Item name="website" label="Website">
                    <Input placeholder="https://aluxerp.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} >
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Shifts" key="shift">
            <Form form={shiftForm} layout="vertical" onValuesChange={onValuesChange} onFinish={handleShiftSave} initialValues={{
              shifts: [
                {
                  shift_name: '',
                  start_time: '',
                  end_time: '',
                },
              ],
            }}>

              <Form.List name="shifts">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, idx) => (
                      <Row
                        className="rowItem mx-0"
                        gutter={[24, 24]}
                        align="middle"
                        key={idx}
                        style={{ width: '100%' }}
                      >
                        <Col flex="auto">
                          <Row gutter={16} align="middle" key={key}>
                            <Form.Item name={[name, "id"]} hidden>
                              <Input />
                            </Form.Item>
                            <Col xs={12} sm={8} xl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'shift_name']}
                                label="Shift Name"
                                rules={[{ required: true, message: "Enter name" }]}
                              >
                                <Input placeholder={`Shift ${idx + 1} name`} />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={8} xl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'start_time']}
                                label="Start time"
                                rules={[{ required: true, message: "Select start time" }]}
                              >
                                <TimePicker format={format} placeholder="Select Start time" />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={8} xl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'end_time']}
                                label="End Time"
                                rules={[{ required: true, message: "Select end time" }]}
                              >
                                <TimePicker format={format} placeholder="Select End Time" />
                              </Form.Item>
                            </Col>
                            <Col xs={12} sm={8} xl={3}>
                              <label>Duration</label>
                              <div>
                                <p style={{ color: '#333' }}>{durationsByIdx[idx] || '—'}</p>
                              </div>
                            </Col>

                            <Col xs={12} sm={8} xl={3}>
                              <Button danger onClick={() => remove(name)}>Remove</Button>
                            </Col>
                            {/* ===== INSERT STATUS BLOCK HERE ===== */}
                            <Col style={{ minWidth: 160, display: 'flex', justifyContent: 'flex-end' }}>
                              {(() => {
                                const rowStatus = saveStatuses[idx];
                                if (!rowStatus) return null;
                                if (rowStatus.status === 'saving') {
                                  return <Tag icon={<LoadingOutlined spin />} color="default">Saving</Tag>;
                                }
                                if (rowStatus.status === 'success') {
                                  return <Tag icon={<CheckCircleOutlined />} color="success">Saved</Tag>;
                                }
                                if (rowStatus.status === 'failed') {
                                  return (
                                    <div style={{ textAlign: 'right' }}>
                                      <Tag icon={<CloseCircleOutlined />} color="error">Failed</Tag>
                                      {rowStatus.msg && <div style={{ color: 'red', marginTop: 6, fontSize: 12, maxWidth: 300 }}>{rowStatus.msg}</div>}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </Col>
                            {/* ===== END STATUS BLOCK ===== */}
                          </Row>
                        </Col>
                      </Row>
                    ))}

                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block disabled={isFullCoverage} >+ Add Shift</Button>
                      {isFullCoverage && (
                        <div style={{ marginTop: 8 }}>
                          <p type="danger">Shifts already cover 24 hours — cannot add more.</p>
                        </div>
                      )}
                    </Form.Item>

                  </>
                )}
              </Form.List>
              <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Financial" key="financial">
            <Form form={financeForm} layout="vertical" onFinish={onSaveFinancialSetting}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="fiscal_year_start" label="Fiscal Year Start" rules={[{ required: true }]}>
                    <Select>
                      <Option value="January">January</Option>
                      <Option value="April">April</Option>
                      <Option value="July">July</Option>
                      <Option value="October">October</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="invoice_prefix" label="Invoice Prefix">
                    <Input placeholder="INV-" />
                  </Form.Item>

                  <Form.Item name="bank_name" label="Bank Name" rules={[
                    { required: true, message: "Please enter bank name" },
                    { min: 3, message: "Bank name must be at least 3 characters" },
                    {
                      pattern: /^[A-Za-z\s.&]+$/,
                      message: "Bank name should contain only letters",
                    },
                  ]}>
                    <Input placeholder="Bank of India" />
                  </Form.Item>

                  <Form.Item name="account_number" label="Bank Account No" rules={[
                    { required: true, message: "Please enter account number" },
                    { pattern: /^[0-9]{9,18}$/, message: "Enter valid account number" },
                  ]}>
                    <Input placeholder="e.g. 123456789012" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="payment_terms" label="Default Payment Terms">
                    <Select>
                      <Option value="7">7 days</Option>
                      <Option value="15">15 days</Option>
                      <Option value="30">30 days</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="rounding_precision" label="Rounding Precision">
                    <Select>
                      <Option value="0.01">0.01</Option>
                      <Option value="0.05">0.05</Option>
                      <Option value="1">1</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="bank_address" label="Bank Address" rules={[{ required: true, message: "Please enter bank address" }]}>
                    <Input placeholder="JagKalyan Marg Andheri East Mumbai" />
                  </Form.Item>

                  <Form.Item name="ifsc_code" label="Bank IFSC Code" rules={[
                    { required: true, message: "Please enter IFSC code" },
                    { pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/, message: "Enter valid IFSC code" },
                  ]}>
                    <Input maxLength={11} placeholder="e.g. HDFC0001234" />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} >
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Tax & Compliance" key="tax">
            <Form form={taxForm} layout="vertical" onFinish={onSaveTaxComplainceSetting}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="tax_registration_no" label="Tax Registration No" rules={[
                    { required: true, message: "Please enter GSTIN" },
                    {
                      pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
                      message: "Enter valid GSTIN (e.g. 24ABCDE1234F1Z5)",
                    },
                  ]}>
                    <Input placeholder="GSTIN / VAT / Tax ID" />
                  </Form.Item>

                  <Form.Item name="default_tax_rate" label="Default Tax Rate (%)" rules={[{ type: "number", transform: (v) => (v ? Number(v) : v) }]}>
                    <Input placeholder="18" />
                  </Form.Item>

                  <Form.Item name="tax_rounding_method" label="Tax Rounding Method">
                    <Select>
                      <Option value="Per_line">Per line</Option>
                      <Option value="On_total">On total</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="pan_number" label="PAN Number" rules={[
                    { required: true, message: "Please enter PAN number" },
                    {
                      pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                      message: "Enter valid PAN (e.g. ABCDE1234F)",
                    },
                  ]}>
                    <Input placeholder="e.g. ABCDE1234F" maxLength={10} />
                  </Form.Item>

                  <Form.Item name="tax_report_email" label="Tax Reports Email" rules={[{ type: "email", message: "Enter a valid email" }]}>
                    <Input placeholder="tax@company.com" />
                  </Form.Item>

                  <Form.Item
                    name="compliance_document"
                    label="Upload Compliance Docs"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e && e.fileList}
                    rules={[{ validator: validateTaxDocuments }]}
                  >
                    <Upload
                      beforeUpload={() => false}
                      multiple
                      accept=".pdf,.doc,.docx"
                      fileList={taxFileList}
                      onChange={handleTaxDocChange}
                    >
                      <Button icon={<UploadOutlined />}>Upload documents</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} >
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Notifications" key="notifications">
            <Form form={notifForm} layout="vertical" onFinish={onSaveNotificationSetting}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="email_notifications" label="Email Notifications" valuePropName="checked">
                    <Switch defaultChecked />
                  </Form.Item>

                  <Form.Item name="smtp_server" label="SMTP Server">
                    <Input placeholder="smtp.mailserver.com" />
                  </Form.Item>

                  <Form.Item name="system_email" label="System Email">
                    <Input placeholder="noreply@company.com" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="push_notifications" label="Push Notifications" valuePropName="checked">
                    <Switch />
                  </Form.Item>

                  <Form.Item name="daily_summary" label="Daily Summary" valuePropName="checked">
                    <Switch defaultChecked />
                  </Form.Item>

                  <Form.Item name="alert_emails" label="Alert Emails">
                    <Select mode="tags" placeholder="Add alert emails" />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} >
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          <TabPane tab="Terms & Condiiton" key="terms_condition">
            <Form form={termsForm} layout="vertical" onFinish={onSaveTermsConditionSetting}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="quotation_terms_and_conditions"
                    label="Quotation Terms & Condition"
                    rules={[
                      { required: true, message: 'Terms & Condition is required' },
                    ]}
                    valuePropName="content"
                  >
                    <ReactQuill
                      value={quotationTermValue}
                      onChange={handleQuotationTermsChange}
                      theme="snow"
                      modules={{
                        toolbar: [
                          [{ header: '1' }, { header: '2' }, { font: [] }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['bold', 'italic', 'underline'],
                          [{ align: [] }],
                          ['link', 'image'],
                          ['blockquote', 'code-block'],
                        ],
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="work_order_terms_and_conditions"
                    label="Work Order Terms & Condition"
                    rules={[
                      { required: true, message: 'Terms & Condition is required' },
                    ]}
                    valuePropName="content"
                  >
                    <ReactQuill
                      value={workOrderTermValue}
                      onChange={handleWorkOrderTermsChange}
                      theme="snow"
                      modules={{
                        toolbar: [
                          [{ header: '1' }, { header: '2' }, { font: [] }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['bold', 'italic', 'underline'],
                          [{ align: [] }],
                          ['link', 'image'],
                          ['blockquote', 'code-block'],
                        ],
                      }}
                    />
                  </Form.Item>

                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="die_terms_and_conditions"
                    label="Die Quotation Terms & Condition"
                    rules={[
                      { required: true, message: 'Terms & Condition is required' },
                    ]}
                    valuePropName="content"
                  >
                    <ReactQuill
                      value={dieQuotationTermValue}
                      onChange={handleQuotationTermsChange}
                      theme="snow"
                      modules={{
                        toolbar: [
                          [{ header: '1' }, { header: '2' }, { font: [] }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['bold', 'italic', 'underline'],
                          [{ align: [] }],
                          ['link', 'image'],
                          ['blockquote', 'code-block'],
                        ],
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="proforma_terms_and_conditions"
                    label="Proforma's Terms & Condition"
                    rules={[
                      { required: true, message: 'Terms & Condition is required' },
                    ]}
                    valuePropName="content"
                  >
                    <ReactQuill
                      value={proformaTermValue}
                      onChange={handleQuotationTermsChange}
                      theme="snow"
                      modules={{
                        toolbar: [
                          [{ header: '1' }, { header: '2' }, { font: [] }],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['bold', 'italic', 'underline'],
                          [{ align: [] }],
                          ['link', 'image'],
                          ['blockquote', 'code-block'],
                        ],
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
               <Row justify="end" align="middle" className="mb-4">
                <Col>
                  <Space>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} >
                      Save
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </TabPane>

          {/* <TabPane tab="Backup & Restore" key="backup">
            <Row gutter={16}>
              <Col span={24}>
                <p>Schedule automatic backups and manage manual export/imports.</p>
                <Space>
                  <Button>Run Backup Now</Button>
                  <Button>Download Latest Backup</Button>
                </Space>
              </Col>
            </Row>
          </TabPane> */}
        </Tabs>
      </div>
    </>
  );
}
